// ---------------------------------------------------------------------------
// DnD Email Editor – canvas render sanitizer
//
// The canvas renders layer/element content inline with dangerouslySetInnerHTML
// (see DndCanvas: "React Canvas (no iframe)"), so anything in that content
// which styles the *document* rather than the block escapes the canvas and
// restyles the whole app — a `<style>` block carrying `body`, `a`, `table` or
// `*` rules turns nav links black, collapses page widths, etc.
//
// Email content legitimately carries such tags (a pasted template, a `<style>`
// typed into an HTML block), so they are NOT removed from the model or from the
// compiled email — only from what gets injected into the app DOM.
// ---------------------------------------------------------------------------

/** Tags that affect the whole document rather than the block they sit in. */
const PAGE_AFFECTING = ['style', 'link', 'script', 'base', 'meta', 'title'];

const DETECT_RE = new RegExp(`<\\s*(?:${PAGE_AFFECTING.join('|')})\\b`, 'i');

/** True when `html` carries a tag that would leak out of the canvas. */
export function hasPageAffectingTags(html: string): boolean {
  return !!html && DETECT_RE.test(html);
}

/**
 * Remove document-level tags from an HTML fragment so it can be injected into
 * the page without restyling the app. Returns the input unchanged when there is
 * nothing to strip (the common case), so this stays cheap on every render.
 */
export function stripPageAffectingTags(html: string): string {
  if (!hasPageAffectingTags(html) || typeof DOMParser === 'undefined') return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const body = doc.body;
    if (!body) return html;
    body.querySelectorAll(PAGE_AFFECTING.join(', ')).forEach((node) => node.remove());
    return body.innerHTML;
  } catch {
    // A parse failure must not blank the block — better to render as-is.
    return html;
  }
}
